import { type FormEvent, useEffect, useRef, useState } from 'react';

import { type SignedInUser, updateDisplayName } from '../api/client.ts';
import { AppHeader } from '../ui/AppHeader.tsx';
import type { HeaderAccount } from '../ui/HeaderMenu.tsx';
import { failureMessage } from './failure.ts';
import { displayNameProblem } from './validation.ts';

export interface AccountViewProps {
  user: SignedInUser;
  onBack: () => void;
  /** Called with the account as the server now has it. */
  onSaved: (user: SignedInUser) => void;
  /** Who is signed in and the control that ends it, in the header's menu. */
  account: HeaderAccount;
}

/**
 * The signed-in user's own account: the display name, which is theirs to change and is what their
 * work is attributed to, and the e-mail address, which is how they sign in and is shown only.
 */
export const AccountView = ({ user, onBack, onSaved, account }: AccountViewProps) => {
  const [name, setName] = useState(user.name);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  /**
   * Whether this screen is still showing. A save that answers after it has gone — the user signed
   * out, or went elsewhere — is not reported to the shell: the account it describes may no longer
   * be the one signed in.
   */
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSaved(false);

    const problem = displayNameProblem(name);
    setError(problem);
    if (problem !== null) {
      return;
    }

    setPending(true);
    void updateDisplayName(name.trim()).then(
      (updated) => {
        if (!mounted.current) {
          return;
        }

        setPending(false);
        setName(updated.name);
        setSaved(true);
        onSaved(updated);
      },
      (failure: unknown) => {
        setPending(false);
        setError(failureMessage(failure));
      },
    );
  };

  return (
    <>
      <AppHeader account={account} />
      <main className="studies account" data-test-id="account-view">
        <div className="studies-header">
          <h1>Your account</h1>
        </div>

        <button type="button" data-test-id="account-back" onClick={onBack}>
          All studies
        </button>

        <form onSubmit={submit}>
          <label htmlFor="account-name">Display name</label>
          <input
            id="account-name"
            data-test-id="account-name"
            autoComplete="name"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setSaved(false);
            }}
            aria-invalid={error !== null}
            aria-describedby={error === null ? undefined : 'account-error'}
            // Held while a save is in flight, so the name the server answers with cannot replace
            // something typed after it was sent.
            readOnly={pending}
            required
          />

          <label htmlFor="account-email">E-mail</label>
          <input
            id="account-email"
            data-test-id="account-email"
            type="email"
            value={user.email}
            readOnly
            aria-describedby="account-email-note"
          />
          <p className="login-note" id="account-email-note">
            This is the address you sign in with. It cannot be changed here.
          </p>

          {error === null ? null : (
            <p className="login-error" id="account-error" data-test-id="account-error" role="alert">
              {error}
            </p>
          )}
          {saved ? (
            <p className="login-note" data-test-id="account-saved" role="status">
              Saved.
            </p>
          ) : null}

          <button type="submit" data-test-id="account-save" disabled={pending}>
            {pending ? 'Saving…' : 'Save'}
          </button>
        </form>
      </main>
    </>
  );
};
